import React, { useEffect, useState } from "react";
import { Button, Card, CardBody, CardHeader } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  Overall_Summary_Payin,
  Overall_Summary_Payout,
} from "../../../redux/slices/payinPayoutSlice";
import { Filter_Data_Transaction } from "../../../redux/slices/transactionSlice";
import { useRole } from "../../../redux/slices/authSlice";
import { DROPDOWN_ALL_VALUE } from "../../../constant";
import { dateFormatter, seperator } from "../../../helperFunctions";
import { getStatusStyle } from "../../../theme";
import { Table, TableCell, TableRow } from "../../../components/Table";
import DropDown from "../../../components/Custom/Forms/DropDown/DropDown";
import DatePicker from "../../../components/Custom/Forms/DatePicker/DatePicker";
import Loader from "../../../components/Custom/Loader";
import StatsHorizontal from "../../../components/widgets/stats/StatsHorizontal";
import Totalcollection from "../../../assets/images/pages/totalcollection.svg";
import Totalpayout from "../../../assets/images/pages/totalpayout.svg";
import Totalbalace from "../../../assets/images/pages/totalbalace.svg";
import Bankbalance from "../../../assets/images/pages/bankbalance.svg";
import PendingIcon from "../../../assets/images/pages/pending-icon.svg";
import SuccessIcon from "../../../assets/images/pages/success-icon.svg";
import "../../Main/Home/Home.scss";
import "./OverallSummary.scss";

// no date by default, so no date is sent and api gives all-time data
const getInitialFilter = () => ({
  startDate: null,
  endDate: null,
  merchantId: DROPDOWN_ALL_VALUE,
  pgName: DROPDOWN_ALL_VALUE,
});

const formatAmount = (amount) => seperator(+amount || 0);
const formatCount = (count) => (+count || 0).toLocaleString("en-IN");
const successRate = (success, total) =>
  +total ? `${((+success / +total) * 100).toFixed(2)}%` : "0.00%";

const amountColumn = (title, name) => ({
  title,
  name,
  Cell: (data) => <>{formatAmount(data?.[name])}</>,
});
const countColumn = (title, name) => ({
  title,
  name,
  Cell: (data) => <>{formatCount(data?.[name])}</>,
});

const TAB_CONFIG = {
  payin: {
    action: Overall_Summary_Payin,
    stateKey: "overallSummaryPayinState",
    pgDataKey: "pg_data",
    cards: (d) => [
      {
        title: "Total Collection",
        value: formatAmount(d?.collection_amount),
        image: Totalcollection,
      },
      {
        title: "Total Fees",
        value: formatAmount(+d?.pg_fees + +d?.associate_fees),
        image: Totalbalace,
      },
      {
        title: "Payable",
        value: formatAmount(d?.payable_amount),
        image: Bankbalance,
      },
      {
        title: "Success",
        value: `${formatCount(d?.success_txn)} / ${formatCount(d?.total_txn)}`,
        count: `(${successRate(d?.success_txn, d?.total_txn)})`,
        image: SuccessIcon,
        isGlyph: true,
      },
    ],
    columns: [
      countColumn("TXN", "total_txn"),
      countColumn("SUCCESS", "success_txn"),
      amountColumn("COLLECTION", "collection_amount"),
      amountColumn("PG FEES", "pg_fees"),
      amountColumn("ASSOCIATE FEES", "associate_fees"),
      amountColumn("PAYABLE", "payable_amount"),
    ],
  },
  payout: {
    action: Overall_Summary_Payout,
    stateKey: "overallSummaryPayoutState",
    pgDataKey: "pgpayout_data",
    cards: (d) => [
      {
        title: "Total Withdrawal",
        value: formatAmount(d?.withdrawal_amount),
        image: Totalpayout,
      },
      {
        title: "Fees",
        value: formatAmount(+d?.payout_fees + +d?.associate_fees),
        image: Totalbalace,
      },
      {
        title: "Pending",
        value: formatAmount(d?.pending_amount),
        count: `(${formatCount(d?.pending_payout)})`,
        image: PendingIcon,
        isGlyph: true,
      },
      {
        title: "Success",
        value: `${formatCount(d?.success_payout)} / ${formatCount(
          d?.total_payout
        )}`,
        count: `(${successRate(d?.success_payout, d?.total_payout)})`,
        image: SuccessIcon,
        isGlyph: true,
      },
    ],
    columns: [
      countColumn("PAYOUT", "total_payout"),
      countColumn("SUCCESS", "success_payout"),
      countColumn("PENDING", "pending_payout"),
      amountColumn("WITHDRAWAL", "withdrawal_amount"),
      amountColumn("PENDING AMOUNT", "pending_amount"),
      amountColumn("PAYOUT FEES", "payout_fees"),
      amountColumn("ASSOCIATE FEES", "associate_fees"),
    ],
  },
};

const pgColumns = [
  {
    title: "PG NAME",
    name: "pg_name",
    Cell: (data) => <>{data?.pg_name || "NA"}</>,
  },
  {
    title: "TYPE",
    name: "pg_type",
    Cell: (data) => {
      const { bgClassName } = getStatusStyle(
        data?.pg_type === "AUTO" ? "Success" : "Processing"
      );
      return <span className={bgClassName}>{data?.pg_type || "NA"}</span>;
    },
  },
];

const SummaryStats = ({ cards, isCompact = false }) => {
  return (
    <div className={`overall-stats-grid ${isCompact ? "compact" : ""}`}>
      {cards?.map((card, index) => (
        <div key={card.title}>
          <StatsHorizontal
            className={`fontstyle greentext ${
              card.isGlyph ? "overall-glyph-icon" : ""
            }`}
            color="primary"
            index={index}
            image={card.image}
            statTitle={card.value}
            count_per={card.count}
            renderStats={<h5 className="fw-bolder mb-0">{card.title}</h5>}
          />
        </div>
      ))}
    </div>
  );
};

const OverallSummaryTab = ({ type, filter }) => {
  const dispatch = useDispatch();
  const config = TAB_CONFIG[type];
  const columns = [...pgColumns, ...config.columns];
  const [filterColumns, setFilterColumns] = useState(
    columns?.map((item) => item?.name)
  );
  const [isLoading, setIsLoading] = useState(false);

  const summaryState = useSelector(
    (state) => state.payinPayout?.[config.stateKey]
  );
  const summary = summaryState?.data || null;

  const onGetSummary = async () => {
    const { startDate, endDate, merchantId, pgName } = filter;
    try {
      setIsLoading(true);
      const payload = {
        filter_data: {
          ...(startDate
            ? {
                startDate: dateFormatter(startDate, "start"),
                endDate: dateFormatter(endDate, "end", startDate),
              }
            : {}),
          merchantId,
          pgName,
        },
      };
      await dispatch(config.action(payload)).unwrap();
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    onGetSummary();
  }, [filter]);

  if (isLoading && !summary) {
    return (
      <div style={{ height: 300 }} className="flex-center">
        <Loader />
      </div>
    );
  }

  const typeWise = [
    { title: "Auto", data: summary?.auto },
    { title: "Manual", data: summary?.manual },
  ]?.filter((item) => item?.data);

  return (
    <div className={`overall-summary-main ${isLoading ? "opacity-25" : ""}`}>
      <Card className="overall-summary-card">
        <CardBody>
          <SummaryStats cards={config.cards(summary?.total)} />
        </CardBody>
      </Card>

      {typeWise?.length ? (
        <div
          className={`overall-type-grid ${typeWise.length === 1 ? "single" : ""}`}
        >
          {typeWise?.map((item) => (
            <Card className="overall-summary-card" key={item.title}>
              <CardBody>
                <h5 className="tab-table-heading mb-50">{item.title}</h5>
                <SummaryStats
                  cards={config.cards(item.data)}
                  isCompact={typeWise.length > 1}
                />
              </CardBody>
            </Card>
          ))}
        </div>
      ) : null}

      <Card>
        <CardHeader>
          <h5 className="tab-table-heading">PG Wise Summary</h5>
        </CardHeader>
        <Table
          columns={columns}
          filterColumns={filterColumns}
          isLoading={isLoading && !summary}
          data={summary?.pg_wise || []}
          isData={!!summary?.pg_wise?.length}
          isExpandable={false}
          minHeight={200}
          onColumnsChange={(columns) => {
            setFilterColumns(columns);
          }}
        >
          {summary?.pg_wise?.map((item, index) => {
            return (
              <TableRow
                columns={columns}
                filterColumns={filterColumns}
                isExpandable={false}
                item={item}
                key={index}
              />
            );
          })}
          {summary?.pg_wise?.length ? (
            <tr className="lasttablerow">
              {columns
                ?.filter((column) => filterColumns?.includes(column?.name))
                ?.map((column, index) => (
                  <TableCell key={column.name}>
                    {index === 0
                      ? "Total"
                      : pgColumns.includes(column)
                      ? ""
                      : column.Cell(summary?.total)}
                  </TableCell>
                ))}
            </tr>
          ) : null}
        </Table>
      </Card>
    </div>
  );
};

const OverallSummary = () => {
  const dispatch = useDispatch();
  const [currentTab, setCurrentTab] = useState(0);
  const [filter, setFilter] = useState(getInitialFilter);
  const [appliedFilter, setAppliedFilter] = useState(getInitialFilter);
  const role = useRole("Overall Summary");

  const { transactionFilterState } = useSelector((state) => state.transaction);

  const tabsData = [
    { title: "Payin", type: "payin" },
    { title: "Payout", type: "payout" },
  ];
  const currentType = tabsData[currentTab]?.type;

  const prepareMerchantOptions = () => {
    const options = transactionFilterState?.cilent_data
      ?.slice()
      ?.sort((a, b) => a?.merchantName?.localeCompare(b?.merchantName))
      ?.map((val) => ({ value: val?.merchantId, label: val?.merchantName }));
    return options || [];
  };

  const preparePgOptions = () => {
    const options = transactionFilterState?.[TAB_CONFIG[currentType].pgDataKey]
      ?.slice()
      ?.sort((a, b) => a?.pg_name?.localeCompare(b?.pg_name))
      ?.map((val) => ({ value: val?.pg_name, label: val?.pg_name }));
    return options || [];
  };

  useEffect(() => {
    if (
      role.includes("overall-summary-list") &&
      !transactionFilterState?.pg_data
    ) {
      dispatch(Filter_Data_Transaction());
    }
  }, []);

  if (!role.includes("overall-summary-list")) {
    return;
  }

  return (
    <div className="home-main-div">
      <Card>
        <CardHeader className="flex-column align-items-start">
          <div className="d-flex align-items-center w-100 justify-content-between">
            <div>
              <h4 className="main-title">
                <b>Overall Summary</b>
              </h4>
            </div>
            <div
              className="d-flex my-xl-auto right-content align-items-end button-space"
              style={{ flexWrap: "wrap" }}
            >
              {tabsData?.map((tab, index) => {
                return (
                  <Button
                    key={tab.type}
                    className={
                      currentTab === index ? "btn-primary" : "filterbtn"
                    }
                    color={currentTab === index ? "btn-primary" : ""}
                    onClick={() => {
                      setCurrentTab(index);
                      // PG list is different for payin and payout
                      setFilter({ ...filter, pgName: DROPDOWN_ALL_VALUE });
                      setAppliedFilter({
                        ...appliedFilter,
                        pgName: DROPDOWN_ALL_VALUE,
                      });
                    }}
                  >
                    {tab.title}
                  </Button>
                );
              })}
            </div>
          </div>
          <div
            className="d-flex align-items-end inputgap mt-1"
            style={{ flexWrap: "wrap" }}
          >
            <DropDown
              label="Merchant"
              options={prepareMerchantOptions()}
              isDefaultOption={DROPDOWN_ALL_VALUE}
              value={filter?.merchantId}
              onChange={(selectedOption) => {
                setFilter({ ...filter, merchantId: selectedOption?.value });
              }}
            />
            <DropDown
              label="PG"
              options={preparePgOptions()}
              isDefaultOption={DROPDOWN_ALL_VALUE}
              value={filter?.pgName}
              onChange={(selectedOption) => {
                setFilter({ ...filter, pgName: selectedOption?.value });
              }}
            />
            <DatePicker
              label="Date"
              maxDate={new Date()}
              placeholder="All Time"
              selectsRange
              startDate={filter?.startDate}
              endDate={filter?.endDate}
              onChange={(selectedDate) => {
                setFilter({
                  ...filter,
                  startDate: selectedDate?.[0] || null,
                  endDate: selectedDate?.[1] || null,
                });
              }}
            />
            <Button
              color="primary"
              type="button"
              onClick={() => setAppliedFilter({ ...filter })}
            >
              Apply
            </Button>
            <Button
              className="filterbtn"
              type="button"
              onClick={() => {
                setFilter(getInitialFilter());
                setAppliedFilter(getInitialFilter());
              }}
            >
              Clear
            </Button>
          </div>
        </CardHeader>
      </Card>
      <OverallSummaryTab
        key={currentType}
        type={currentType}
        filter={appliedFilter}
      />
    </div>
  );
};

export default OverallSummary;
